import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { type NavItem } from '@/lib/layout.config';
import { SidebarNavGroup } from '@/shared/components/Layout/SidebarNavGroup/SidebarNavGroup';
import { SidebarNavLink } from '@/shared/components/Layout/SidebarNavLink/SidebarNavLink';
import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';
import { resolveActiveNavPath } from '@/shared/utils/resolve-active-nav-path/resolve-active-nav-path.util';

export interface SidebarNavItemProps {
  item: NavItem;
  /** Collapsed rail: icons only, labels carried by the accessible name. */
  isCollapsed: boolean;
}

/**
 * One sidebar entry, permission-gated (N22).
 *
 * An item the operator cannot use is not rendered at all — a disabled nav row
 * invites the click it is going to refuse. A PARENT disappears once every one of
 * its children has: a group heading with nothing under it is a dead end.
 *
 * On the icon rail a group has nowhere to unfold, so it is drawn as one link to
 * its front door — the `to` every group carries for exactly this — and it is
 * current whenever any of its children is, since `/venues` is not under
 * `/meetings` but is under the Meetings group.
 */
export function SidebarNavItem({ item, isCollapsed }: SidebarNavItemProps) {
  const { t } = useTranslation('common');
  const { canAny } = useHasPermission();
  const { pathname } = useLocation();

  const isAllowed = (candidate: NavItem): boolean =>
    candidate.anyOf === undefined || canAny(candidate.anyOf);

  if (!isAllowed(item)) return null;

  const children = (item.children ?? []).filter(isAllowed);
  // A group whose children are all hidden leads nowhere.
  if (item.children !== undefined && children.length === 0) return null;

  const label = t(item.labelKey);

  if (children.length > 0 && !isCollapsed) {
    return (
      <li>
        <SidebarNavGroup item={item} items={children} label={label} />
      </li>
    );
  }

  const isActive =
    resolveActiveNavPath([item.to, ...children.map((child) => child.to)], pathname) !== undefined;

  return (
    <li>
      <SidebarNavLink item={item} label={label} isActive={isActive} isCollapsed={isCollapsed} />
    </li>
  );
}
